import type { CSSProperties, JSX } from 'react'

import { Flex, Heading, Progress, Text } from '@buffer-mono/popcorn'

import type { CommentsScoreData } from '~publish/pages/Community/hooks/scoring/useCommentsScore'
import { getScoreColors } from '~publish/pages/Community/util/commentScoreTheme'

import styles from './CommentScoreHoverCard.module.css'

const METRICS = [
  { label: 'Response rate', key: 'responseRate' },
  { label: 'Response speed', key: 'responseSpeed' },
  { label: 'Consistency', key: 'consistency' },
] as const

interface ProgressColorStyle extends CSSProperties {
  '--progress-fg-color'?: string
  '--progress-value'?: string
  '--progress-max'?: string
}

/**
 * Aggregate breakdown of the comment score (response rate, speed and
 * consistency) shown in the Comment Score scorecard hover card, mirroring the
 * breakdown and score-based colors on the Community page.
 */
export function CommentScoreHoverCard({
  score,
}: {
  score: CommentsScoreData
}): JSX.Element {
  const period = score.currentPeriodScore
  return (
    <Flex direction="column" gap="sm" className={styles.container}>
      <div>
        <Heading as="h2" size="xs">
          Comment score
        </Heading>
        <Text size="sm" color="subtle">
          Your average response rate, speed, and consistency replying to
          comments.
        </Text>
      </div>
      <Flex direction="column" gap="sm" fullWidth>
        {METRICS.map(({ label, key }) => {
          const value = Math.round(period[key].percentage)
          // Match the Community page: color the bar by its own score level.
          const progressStyle: ProgressColorStyle = {
            '--progress-fg-color': getScoreColors(value).progressColor,
            '--progress-value': value.toString(),
            '--progress-max': '100',
          }
          return (
            <Flex key={key} direction="column" gap="3xs" fullWidth>
              <Flex justify="between" align="center" fullWidth>
                <Text size="sm">{label}</Text>
                <Text size="sm" color="subtle">
                  {value}%
                </Text>
              </Flex>
              <Progress
                value={value}
                size="small"
                variant="classic"
                style={progressStyle}
              />
            </Flex>
          )
        })}
      </Flex>
    </Flex>
  )
}
